import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { ReactSVG } from "react-svg";
import NewToolTip from "../../../../common/New-ToolTip";
import { hasActiveInterventionLink } from "../../../Patient-Menu/Interventions/data";

const MedicationSidebarList = ({
  medications = [],
  title,
  isExpanded = true,
  onToggleExpanded,
  onMedicationClick,
  onExpandMedication,
  expandedMedications = [],
  hoveredMed,
  onMouseEnter,
  onMouseLeave,
  menuMedication,
  onMenuOpen,
  selectedMedication,
  isMatchingDrug,
  medicationDetails,
  drugType,
  isIntervention = false,
}) => {
  const [focusItem, setFocusItem] = useState(null);
  const [showTooltipFor, setShowTooltipFor] = useState(null);
  const tooltipTimeouts = useRef({});

  const interventions = useSelector((state) => state.intervention.interventions || []);
  const selectedMedicationId = useSelector((state) => state.medications.selectedMedicationId);
  const hasInterventionLink = (med) =>
    hasActiveInterventionLink(med, interventions);

  const handleMenuOpen = (event, med) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuData = {
      top: rect.top + window.scrollY,
      left: rect.right + 10,
    };
    onMenuOpen(event, med, menuData);
  };

  // Tooltip with 1-second delay to prevent busy UI when scrolling
  const handleMedicationNameMouseEnter = (medId) => {
    clearTimeout(tooltipTimeouts.current[medId]);
    tooltipTimeouts.current[medId] = setTimeout(() => setShowTooltipFor(medId), 700);
  };

  const handleMedicationNameMouseLeave = (medId) => {
    clearTimeout(tooltipTimeouts.current[medId]);
    delete tooltipTimeouts.current[medId];
    setShowTooltipFor(null);
  };

  const handleMedicationNameDoubleClick = (medId) => {
    clearTimeout(tooltipTimeouts.current[medId]);
    delete tooltipTimeouts.current[medId];
    setShowTooltipFor(medId);
  };

  useEffect(() => () => {
    Object.values(tooltipTimeouts.current).forEach(clearTimeout);
  }, []);

  // Keep local focus highlight in sync with global selection, including explicit deselection when selectedMedicationId becomes null.
  useEffect(() => {
    setFocusItem(selectedMedicationId ?? null);
  }, [selectedMedicationId]);

  return (
    <div className="flex flex-col">
      <div
        className={`cursor-pointer rounded-[4px] flex justify-between h-[46px] pl-[28px] pr-[10px] border-[1px] border-gray-100 ${ isExpanded ? "border-b-0" : ""} ${ isExpanded ? "bg-gray-50" : "bg-gray-10" } items-center mt-2 flex-shrink-0`}
        onClick={onToggleExpanded}
      >
        <p className={`text-gray-800 text-base ${ isExpanded ? "font-semibold" : "" }`}>{title}</p>
        <ChevronDownIcon
          className={`w-4 h-4 ${isExpanded ? "-rotate-180" : ""}`}
        />
      </div>
      {isExpanded && (
        <>
          <ul
            className="divide-y divide-gray-100 border-y border-y-gray-100 overflow-y-auto pt-3 w-full max-h-[600px]"
            style={{ maxHeight: '600px' }}
            onMouseLeave={onMouseLeave} 
          >
            {medications.length > 0 ? (
              medications.map((med) => (
                (() => {
                  const isSelected =
                    expandedMedications.includes(med.id) ||
                    focusItem === med.id ||
                    (isMatchingDrug && isMatchingDrug(med)) ||
                    selectedMedication?.id === med.id;

                  return (
                <li
                  key={med.id}
                  className="cursor-pointer text-sm text-teal-600 w-full list-none"
                  onClick={(e) => {
                    setFocusItem(med.id);
                    onMedicationClick(med);
                    e.currentTarget?.scrollIntoView?.({
                      block: "nearest",
                      behavior: "smooth",
                    });
                  }}
                  onMouseEnter={() => {
                    onMouseEnter(med.id);
                  }}
                >
                  <>
                  <div
                    className={`w-full ${isSelected ? "bg-blue-100 hover:bg-blue-100" : "hover:bg-gray-10"}`}
                  >
                    <div
                      data-medication-tooltip={`${drugType || "default"}-${med.id}`}
                      onMouseEnter={() => { handleMedicationNameMouseEnter(med.id); onMouseEnter && onMouseEnter(med.id); }}
                      onMouseLeave={() => { handleMedicationNameMouseLeave(med.id); onMouseLeave && onMouseLeave(); }}
                      onDoubleClick={() => handleMedicationNameDoubleClick(med.id)}
                      className={`pr-[10px] flex w-full items-center py-[10px] ${
                        med.status === "inactive" && "bg-red-50"
                      }`}
                    >
                      <div className="flex-1 min-w-0 mr-3 text-left overflow-hidden">
                        <span className="flex items-center min-w-0 gap-[10px] px-[2px]">
                          <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                            {isIntervention && hasInterventionLink(med) ? (
                              <ReactSVG
                                src="/icons/intervention-sm.svg"
                                className="w-4 h-4"
                              />
                            ) : null}
                          </span>
                          <span
                            className="block text-left truncate"
                          >
                            {med.displayName}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 flex-shrink-0">
                        {(hoveredMed === med.id || menuMedication?.id === med.id) && (
                          <>
                            <Menu as="div" className="relative">
                              <Menu.Button
                                onClick={(e) => {
                                  setFocusItem(med.id);
                                  handleMenuOpen(e, med);
                                }}
                              >
                                <ReactSVG
                                  src={`/icons/triple-dot.svg`}
                                  className={`flex !items-center py-2 px-3 rounded-md  ${
                                    med.id === menuMedication?.id && "bg-gray-300"
                                  }`}
                                />
                              </Menu.Button>
                            </Menu>
                          </>
                        )}
                        <button
                          className=""
                          onClick={() => {
                            onExpandMedication(med.id);
                          }}
                        >
                          {!expandedMedications.includes(med.id) && (
                            <ChevronDownIcon className="w-4 h-4" strokeWidth={2} />
                          )}
                          {expandedMedications.includes(med.id) && (
                            <ChevronUpIcon className="w-4 h-4" strokeWidth={2} />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  {expandedMedications.includes(med.id) && medicationDetails(med)}
                  <NewToolTip
                    id={`medication-sidebar-name-${drugType || "default"}-${med.id}`}
                    anchorSelect={`[data-medication-tooltip="${drugType || "default"}-${med.id}"]`}
                    place="bottom-start"
                    isOpen={showTooltipFor === med.id}
                    render={() => (
                      <div className="bg-teal-25 p-2 max-w-md flex flex-col">
                        <p className="text-teal-300 text-left text-body-sm font-normal">
                          {med.displayName}
                        </p>
                        {(med?.manufacturerStoredType === "Small" && med?.manufacturerCode) ||
                        (med?.manufacturerStoredType === "Full" && med?.manufacturerName) ? (
                          <p className="text-teal-300 text-left text-body-sm font-normal">
                            {med?.manufacturerStoredType === "Small" && med?.manufacturerCode
                              ? ` (${med?.manufacturerCode})`
                              : ` (${med?.manufacturerName})`}
                          </p>
                        ) : null}
                      </div>
                    )}
                  />
                  </>
                </li>
                  );
                })()
              ))
            ) : (
              <li className="pl-7">
                <div className="flex items-center justify-start h-[50px] text-sm font-normal">
                  <span>No Records Found</span>
                </div>
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
};

export default MedicationSidebarList;
